/**
 * AvailabilityWidget ✦ iter223
 *
 * Customer-facing "Current Availability" panel. Mounts above the
 * Check Availability CTA on /photobooth-hire and on every regional
 * landing page. Pulls /api/inventory live so the urgency reflects
 * confirmed bookings without a page reload.
 *
 * Status mapping (locked in by the backend):
 *   green  · 3+ available  · gold-green tint  · "N Available"
 *   amber  · 2 available   · gold tint        · "N Available"
 *   orange · 1 available   · warm-gold tint   · "Only 1 Remaining"
 *   red    · 0 available   · muted brick      · "Fully Booked"
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const SERIF = 'Georgia, "Cormorant Garamond", "Times New Roman", serif';
const GOLD = '#D4AF5E';
const GOLD_FAINT = 'rgba(212,170,82,0.22)';
const IVORY = '#FBEED0';
const IVORY_DIM = 'rgba(251,238,208,0.62)';

const STATUS_STYLE = {
  green:  { dot: '#7DB58C', border: 'rgba(125,181,140,0.32)', text: '#CFE6CC' },
  amber:  { dot: '#E2C77B', border: 'rgba(226,199,123,0.34)', text: '#F4E2A8' },
  orange: { dot: '#E39A4F', border: 'rgba(227,154,79,0.40)', text: '#F6CC92' },
  red:    { dot: '#B86F6F', border: 'rgba(184,111,111,0.36)', text: '#E6B5B5' },
};

const AvailabilityWidget = ({ compact = false }) => {
  const [items, setItems] = useState([]);
  const [year, setYear] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/inventory`);
        if (cancelled) return;
        setItems(r.data.items || []);
        setYear(r.data.year);
      } catch {/* hide silently */}
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || items.length === 0) return null;

  return (
    <div
      data-testid="availability-widget"
      style={{
        background: 'rgba(20,12,6,0.55)',
        border: `1px solid ${GOLD_FAINT}`,
        borderRadius: 14,
        padding: 'clamp(22px, 3vw, 32px) clamp(20px, 3vw, 30px)',
        maxWidth: 540, margin: '0 auto',
        textAlign: 'left',
        boxShadow: '0 24px 60px rgba(0,0,0,0.35)',
        backdropFilter: 'blur(10px)',
      }}
    >
      <div style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11, letterSpacing: '0.28em', textTransform: 'uppercase',
        color: GOLD, marginBottom: 10,
      }}>✦ Current Availability{year ? ` · ${year}` : ''}</div>
      <h3 style={{
        fontFamily: SERIF, fontWeight: 500, fontSize: 22,
        color: IVORY, margin: '0 0 18px', letterSpacing: '-0.005em',
      }}>What&apos;s available right now</h3>

      <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
        {items.map((it) => {
          const s = STATUS_STYLE[it.status] || STATUS_STYLE.amber;
          const isUrgent = it.status === 'orange' || it.status === 'red';
          return (
            <li
              key={it.slug}
              data-testid={`availability-row-${it.slug}`}
              data-status={it.status}
              style={{
                display: 'flex', alignItems: 'center',
                justifyContent: 'space-between', gap: 12,
                padding: '10px 0',
                borderTop: `1px solid rgba(255,255,255,0.06)`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                <span aria-hidden style={{
                  width: 10, height: 10, borderRadius: 999,
                  background: s.dot, flexShrink: 0,
                  boxShadow: `0 0 12px ${s.dot}88`,
                }} />
                <span style={{
                  fontFamily: SERIF, fontSize: compact ? 13.5 : 14.5,
                  color: IVORY, lineHeight: 1.35,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{it.name}</span>
              </div>
              <span
                data-testid={`availability-label-${it.slug}`}
                style={{
                  fontFamily: SERIF,
                  fontStyle: isUrgent ? 'italic' : 'normal',
                  fontWeight: isUrgent ? 600 : 500,
                  fontSize: compact ? 12.5 : 13,
                  color: s.text,
                  padding: '4px 12px',
                  borderRadius: 999,
                  border: `1px solid ${s.border}`,
                  whiteSpace: 'nowrap',
                  letterSpacing: '0.02em',
                }}
              >{it.label}</span>
            </li>
          );
        })}
      </ul>

      <p style={{
        fontFamily: SERIF, fontStyle: 'italic',
        fontSize: 11.5, color: IVORY_DIM,
        margin: '16px 0 0', textAlign: 'center', letterSpacing: '0.02em',
      }}>Availability updates automatically as bookings are confirmed.</p>
    </div>
  );
};

export default AvailabilityWidget;
